import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = ['en', 'de'];
const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'ivasi_language';

export const LanguageProvider = ({ children }) => {
  const [currentLang, setCurrentLang] = useState(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  // Detect browser language
  const detectBrowserLanguage = () => {
    const browserLang = navigator.language.split('-')[0];
    return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE;
  };

  // Initialize language on mount
  useEffect(() => {
    // Priority: URL → localStorage → browser → default
    const pathParts = location.pathname.split('/').filter(Boolean);
    const urlLang = pathParts[0];

    let detectedLang = DEFAULT_LANGUAGE;

    if (SUPPORTED_LANGUAGES.includes(urlLang)) {
      detectedLang = urlLang;
    } else {
      const storedLang = localStorage.getItem(STORAGE_KEY);
      if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang)) {
        detectedLang = storedLang;
      } else {
        detectedLang = detectBrowserLanguage();
      }
    }

    setCurrentLang(detectedLang);
    loadTranslations(detectedLang);
    updateHtmlLang(detectedLang);
  }, [location.pathname]);

  // Load translation files
  const loadTranslations = async (lang) => {
    try {
      const translationModule = await import(`../translations/${lang}.json`);
      setTranslations(translationModule.default || translationModule);
    } catch (error) {
      console.warn(`Failed to load translations for ${lang}, falling back to English`);
      if (lang !== 'en') {
        const fallbackModule = await import(`../translations/en.json`);
        setTranslations(fallbackModule.default || fallbackModule);
      }
    }
  };

  // Update HTML lang attribute
  const updateHtmlLang = (lang) => {
    document.documentElement.lang = lang;
  };

  // Change language
  const changeLanguage = (newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) {
      console.warn(`Unsupported language: ${newLang}`);
      return;
    }

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, newLang);

    // Update state
    setCurrentLang(newLang);
    loadTranslations(newLang);
    updateHtmlLang(newLang);

    // Update URL
    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentUrlLang = SUPPORTED_LANGUAGES.includes(pathParts[0]) ? pathParts[0] : null;

    let newPath;
    if (currentUrlLang) {
      // Replace existing language in URL
      pathParts[0] = newLang === 'en' ? null : newLang;
      newPath = '/' + pathParts.filter(Boolean).join('/');
    } else {
      // Add language to URL if not English
      newPath = newLang === 'en' ? location.pathname : `/${newLang}${location.pathname}`;
    }

    // Handle root path
    if (newPath === '/' || newPath === '') {
      newPath = newLang === 'en' ? '/main' : `/${newLang}/main`;
    }

    navigate(newPath, { replace: true });
  };

  // Translation helper function
  const t = (key) => {
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }

    return value || key;
  };

  // Get localized path
  const getLocalizedPath = (path) => {
    if (currentLang === 'en') {
      return path;
    }
    return `/${currentLang}${path}`;
  };

  const value = {
    currentLang,
    changeLanguage,
    t,
    getLocalizedPath,
    translations,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
